import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiSun, FiMoon, FiCheck, FiZap, FiServer, FiUser, FiArrowRight, FiLink, FiGlobe, FiHome, FiHash, FiFileText, FiUsers } from 'react-icons/fi';
import { useTheme, ThemeMode } from '../../context/ThemeContext';
import { ROUTES } from '../../app/routes';
import { isCommandCenterEnabled, isVoiceChatEnabled } from '../../utils/featureAccess';
import VoiceChatSettingsSection from '../voicechat/VoiceChatSettingsSection';
import { isAgencyAdmin } from '../../services/teamService';
import '../../styles/Settings.css';
const SettingsPage: React.FC = () => {
    const { theme, setTheme } = useTheme();
    const navigate = useNavigate();
    const navCards = [
        ...(isCommandCenterEnabled() ? [{
                icon: <FiZap size={20}/>,
                accent: '#f59e0b',
                bg: 'rgba(245,158,11,0.12)',
                label: 'Connected Apps',
                desc: 'Connect Gmail, Calendar, Slack, and more for AI automation',
                route: ROUTES.SETTINGS_INTEGRATIONS,
            }] : []),
        {
            icon: <FiServer size={20}/>,
            accent: '#22d3ee',
            bg: 'rgba(34,211,238,0.12)',
            label: 'MCP Servers',
            desc: 'Connect and manage your Model Context Protocol servers',
            route: ROUTES.SETTINGS_MCP,
        },
        {
            icon: <FiLink size={20}/>,
            accent: '#f43f5e',
            bg: 'rgba(244,63,94,0.12)',
            label: 'Webhooks',
            desc: 'Get a secret key to create commands or trigger routines from outside Momentum AMP',
            route: ROUTES.SETTINGS_WEBHOOKS,
        },
        // Hidden for now: Digital Workforce (ROUTES.SETTINGS_HARNESS_TYPE) and Security cards.
        // Who's on the team is managed in the Admin Dashboard.
        ...(isAgencyAdmin() ? [{
            icon: <FiUsers size={20}/>,
            accent: '#818cf8',
            bg: 'rgba(129,140,248,0.12)',
            label: 'Team access',
            desc: 'Choose who from your agency works in Momentum AMP. Opens the Admin Dashboard.',
            route: ROUTES.AGENCY_ADMIN,
        }] : []),
        {
            icon: <FiUser size={20}/>,
            accent: '#818cf8',
            bg: 'rgba(129,140,248,0.12)',
            label: 'Profile Settings',
            desc: 'Manage your account name, avatar, and preferences',
            route: ROUTES.PROFILE,
        },
        {
            icon: <FiHome size={20}/>,
            accent: '#2A65F2',
            bg: 'rgba(42,101,242,0.12)',
            label: 'Agency',
            desc: 'Update your agency name, contact details, address, and license',
            route: `${ROUTES.PROFILE}?tab=agency`,
        },
        {
            icon: <FiHash size={20}/>,
            accent: '#FF6820',
            bg: 'rgba(255,104,32,0.12)',
            label: 'MCP Keys',
            desc: 'Generate and manage keys for connecting MCP clients',
            route: `${ROUTES.PROFILE}?tab=api-keys`,
        },
        {
            icon: <FiFileText size={20}/>,
            accent: '#FEAD0F',
            bg: 'rgba(254,173,15,0.12)',
            label: 'Agency Documents',
            desc: 'Upload and manage agency documents, licenses, and certificates',
            route: `${ROUTES.PROFILE}?tab=agency-docs`,
        },
        {
            icon: <FiGlobe size={20}/>,
            accent: '#34d399',
            bg: 'rgba(52,211,153,0.12)',
            label: 'Allowed Domains',
            desc: 'Choose which websites your Digital Workforce agents can open',
            route: `${ROUTES.PROFILE}?tab=allowed-domains`,
        },
    ];
    const cardVariants = {
        hidden: { opacity: 0, y: 16 },
        visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.07, duration: 0.3 } }),
    };
    return (<div className="s2-page">
      <motion.div className="s2-container" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>

        <section className="s2-top-header">
          <div className="s2-top-header__text">
            <h1 className="s2-top-header__title">Settings</h1>
            <p className="s2-top-header__subtitle">Manage appearance and open key configuration panels for your workspace.</p>
          </div>
          <div className="s2-top-header__actions">
            <button className="s2-top-header__nav-btn" onClick={() => navigate(ROUTES.PROFILE)}>
              <FiUser size={14}/>
              <span>Profile</span>
            </button>
          
          </div>
        </section>

        
        <motion.section className="s2-section" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
          <div className="s2-section-label">
            <span className="s2-section-dot s2-section-dot--purple"/>
            Appearance
          </div>

          <div className="s2-appearance-card">
            <div className="s2-appearance-left">
              <div className="s2-appearance-icon-wrap">
                {theme === 'dark' ? <FiMoon size={20}/> : <FiSun size={20}/>}
              </div>
              <div>
                <div className="s2-appearance-title">Theme</div>
                <div className="s2-appearance-sub">Choose your preferred visual mode</div>
              </div>
            </div>
            <div className="s2-theme-toggle">
              {(['light', 'dark'] as ThemeMode[]).map(t => (<button key={t} className={`s2-theme-btn${theme === t ? ' s2-theme-btn--active' : ''}`} onClick={() => setTheme(t)}>
                  {t === 'light' ? <FiSun size={14}/> : <FiMoon size={14}/>}
                  <span>{t === 'light' ? 'Light' : 'Dark'}</span>
                  {theme === t && <FiCheck size={12} className="s2-theme-check"/>}
                </button>))}
            </div>
          </div>
        </motion.section>


        <div className="s2-section-label s2-section-label--tight">
          <span className="s2-section-dot s2-section-dot--cyan"/>
          Quick Access
        </div>

        <div className="s2-grid">
          {navCards.map((card, i) => (<motion.div key={card.label} className="s2-nav-card" custom={i} variants={cardVariants} initial="hidden" animate="visible" whileHover={{ y: -3, boxShadow: `0 12px 40px rgba(0,0,0,0.25)` }} style={{ '--card-accent': card.accent } as React.CSSProperties} onClick={() => navigate(card.route)}>
              <div className="s2-nav-card__icon" style={{ background: card.bg, color: card.accent }}>
                {card.icon}
              </div>
              <div className="s2-nav-card__body">
                <div className="s2-nav-card__label">{card.label}</div>
                <div className="s2-nav-card__desc">{card.desc}</div>
              </div>
              <div className="s2-nav-card__meta">
                <span className="s2-nav-card__open">Open</span>
                <div className="s2-nav-card__arrow" style={{ color: card.accent }}>
                  <FiArrowRight size={16}/>
                </div>
              </div>
            </motion.div>))}
        </div>

        {isVoiceChatEnabled() && <VoiceChatSettingsSection />}

      </motion.div>
    </div>);
};
export default SettingsPage;
